import {default as React, useState} from 'react';
import { Theme, useTheme} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import { makeStyles } from 'tss-react/mui';
import classnames from 'classnames';
import AsyncSelect from 'react-select/async';
import {AreaSearchTypes} from '../../constants/areasearchTypes';
import {useLanguage} from '../../i18n';
import areaService, {AreaSearchResult} from '../../services/areaService';
import './searchableSelect.scss';
import {Area} from '../../model/area/area';
import {ReindeerLocation} from '../../model/area/reindeerLocation';
import {
    MultiValueGenericProps,
    components,
    MultiValueRemoveProps, ActionMeta
} from 'react-select';

type Location = Area | ReindeerLocation;

type OwnProps = {
    areas: Location[]
    onChange: (selectedOption: Location | Location[], action: ActionMeta<Location>) => void;
    placeholder: string;
    noOptionsMessage: string;
    filterMenu: boolean;
    isMultiSelect: boolean;
    type: AreaSearchTypes;
};

function areaSearch(input: string, type: AreaSearchTypes): Promise<AreaSearchResult[]> {
    switch (type) {
        case AreaSearchTypes.all:
            return areaService.getAllAreas(input);
        case AreaSearchTypes.norway:
            return areaService.getNorwayAreas(input);
        case AreaSearchTypes.reindeer:
            return areaService.getReindeerAreas(input);
        default:
            throw Error(`Unhandled case: ${type}`);
    }
}

const useStyles = makeStyles()((theme: Theme) =>
    ({
        root: {
            border: `1px solid ${theme.palette.grey[400]}`,
            minHeight: '44px',
            backgroundColor: theme.palette.background.paper,
        },
        rootMobile: {
            flexGrow: 1,
            minWidth: 290,
        },
        divider: {
            height: theme.spacing(2),
        },
        icon: {
            color: theme.palette.primary.main
        }
    }));

function MultiValueContainer (props: MultiValueGenericProps<Location> ): React.ReactElement {
    return (
        <div tabIndex={0}>
            <components.MultiValueContainer {...props}/>
        </div>
    );
}

const MultiValueRemove = (props: MultiValueRemoveProps<Location>) => {
    return (
            <components.MultiValueRemove {...props}>
                <CancelIcon />
            </components.MultiValueRemove>
    );
};

const adjustedComponents = {
    MultiValueContainer,
    MultiValueRemove
};

export default function AreaSearchableSelect(props: OwnProps): React.ReactElement {
    const theme = useTheme();
    const [inputText, setInputText] = useState<string>('');
    const {classes} = useStyles();
    const searchWrapperClass = classnames(classes.root, 'locationSearch');
    const iconClass = classnames(classes.icon, 'icon-button-wrapper');
    const SearchableSelectClasses = classnames(classes, 'searchable-select-container');
    const [activeTimerId, setActiveTimerId] = useState<number>();
    const l = useLanguage();
    const selectStyles = {
        control: (base, state) => ({
            ...base,
            border: state.isFocused ? 'none' : 'none',
            boxShadow: state.isFocused && 'none',
        }),
        valueContainer: (base) => ({
            display: 'flex',
            flexWrap: 'wrap' as 'wrap',
            flex: 1,
            alignItems: 'center',
            overflow: 'hidden',
            minHeight: '42px',
        }),
        menu: (base) => ({
            ...base,
            position: 'absolute',
            zIndex: 1,
            marginTop: '1px',
            left: '-38px',
            right: 0,
            width: 'unset',
            borderRadius: 0
        }),
        multiValue: (base) => ({
            ...base,
            borderRadius: '20px',
            margin: theme.spacing(0.5, 0.25),
            padding: '4px 0px 4px 4px',
            backgroundColor: theme.palette.grey[300],
            '&:focus': {
                backgroundColor: theme.palette.grey[200],
                border: `2px solid ${theme.palette.grey[500]}`,
            },
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: theme.palette.grey[600],
            '&:hover': {
                color: theme.palette.grey[700],
                backgroundColor: 'unset',
            }
        }),
        singleValue: (base) => ({
            ...base,
            whiteSpace: 'normal',
            fontSize: '16px',
            maxWidth: '95%',
            padding: '8px 4px',
        }),
        option: (base, state) => ({
            ...base,
            padding: '8px 12px',
            color: theme.palette.text.primary,
            backgroundColor: state.isSelected ? theme.palette.background.paper : undefined,
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
            }
        }),
        input: (base) => ({
            ...base,
            color: theme.palette.text.primary,
            margin: '0px',
            '& input': {
                font: 'inherit',
            },
        }),
    };

    function getOptionsWithTimeout(input: string): Promise<AreaSearchResult[]> {
        clearTimeout(activeTimerId);
        return new Promise<void>(resolve => {
            const timer = window.setTimeout(function () {
                clear();
                resolve();
            }, 1000);
            setActiveTimerId(timer);
        }).then(() => {
            return areaSearch(input, props.type);
        });
    }

    function clear(): void {
        clearTimeout(activeTimerId);
        setActiveTimerId(activeTimerId);
    }

    return (
        <div className={searchWrapperClass}>
            <span className={iconClass} aria-hidden={true}>
                <SearchIcon/>
            </span>
            <span className="locationSearch-label">
                <AsyncSelect
                    isClearable={true}
                    key={props.type}
                    styles={selectStyles}
                    aria-label={l('ariaLabel.geographySearch')}
                    className={SearchableSelectClasses}
                    inputId="react-select-location"
                    isMulti={props.isMultiSelect}
                    classNamePrefix="searchable-select"
                    cacheOptions
                    defaultOptions
                    value={props.areas}
                    inputValue={inputText}
                    onInputChange={setInputText}
                    placeholder={props.placeholder}
                    loadOptions={getOptionsWithTimeout}
                    onChange={props.onChange}
                    maxMenuHeight={250}
                    components={adjustedComponents}
                    noOptionsMessage={() => props.noOptionsMessage}
                />
            </span>
        </div>
    );
}
